/**
 * FunnelNode — single draggable node in the funnel canvas.
 * Displays page type, traffic numbers, conversion rate, and optional A/B badge.
 */
import {
  Megaphone,
  Layout,
  CreditCard,
  TrendingUp,
  TrendingDown,
  CheckCircle,
  Box,
  Users,
  ArrowDown,
} from "lucide-react";
import type { FunnelNodeData, FunnelNodeType } from "./funnel-types";
import { NODE_TYPE_CONFIG } from "./funnel-types";

// ── Icon resolver ──────────────────────────────────────

const ICON_MAP: Record<string, typeof Megaphone> = {
  megaphone: Megaphone,
  layout: Layout,
  creditCard: CreditCard,
  trendingUp: TrendingUp,
  trendingDown: TrendingDown,
  checkCircle: CheckCircle,
  box: Box,
};

const getNodeIcon = (type: FunnelNodeType) => {
  const config = NODE_TYPE_CONFIG[type];
  return ICON_MAP[config.iconName] ?? Box;
};

// ── Currency formatter ─────────────────────────────────

const formatRevenue = (value: number): string =>
  value.toLocaleString("pl-PL", { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + " zł";

// ── Component ──────────────────────────────────────────

interface FunnelNodeProps {
  node: FunnelNodeData;
  isBeingDragged: boolean;
  onPointerDown: (nodeId: string, event: React.PointerEvent) => void;
}

export default function FunnelNode({ node, isBeingDragged, onPointerDown }: FunnelNodeProps) {
  const config = NODE_TYPE_CONFIG[node.type];
  const Icon = getNodeIcon(node.type);

  return (
    <div
      className={`absolute select-none ${isBeingDragged ? "z-50 scale-105" : "z-10"} transition-shadow`}
      style={{
        left: node.position.x,
        top: node.position.y,
        cursor: isBeingDragged ? "grabbing" : "grab",
      }}
      onPointerDown={(e) => onPointerDown(node.id, e)}
    >
      {/* Traffic badge above */}
      <div className="flex items-center justify-center gap-1 mb-1.5">
        <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/10 border border-white/10">
          <Users className="w-3 h-3 text-white/50" />
          <span className="text-[11px] font-semibold text-white">
            {node.trafficIn.toLocaleString()}
          </span>
        </div>
      </div>

      {/* Node card */}
      <div
        className={`relative w-[180px] rounded-xl border-2 ${config.borderColor} ${config.bgColor} backdrop-blur-sm shadow-lg hover:shadow-xl transition-shadow`}
      >
        {/* A/B badge */}
        {node.variant && (
          <div className="absolute -top-2.5 -right-2.5 w-6 h-6 rounded-full bg-[#ef3e29] flex items-center justify-center shadow-md">
            <span className="text-[10px] font-bold text-white">{node.variant}</span>
          </div>
        )}

        {/* Header */}
        <div className="px-3 py-2.5 border-b border-white/[0.06]">
          <div className="flex items-center gap-2">
            <div
              className={`w-7 h-7 rounded-lg ${config.bgColor} flex items-center justify-center`}
            >
              <Icon className={`w-3.5 h-3.5 ${config.color}`} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] text-white/40 uppercase tracking-wider">{config.label}</p>
              <p className="text-xs font-medium text-white truncate">{node.label}</p>
              {node.pageUrl && (
                <p className="text-[9px] text-white/25 truncate mt-0.5" title={node.pageUrl}>
                  {node.pageUrl.replace(/^https?:\/\//, "")}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Page preview */}
        <div className="px-3 py-2">
          <div className="w-full h-14 rounded-md overflow-hidden border border-white/[0.06] bg-white/[0.04] relative">
            {node.pageUrl ? (
              <iframe
                src={node.pageUrl}
                title={node.label}
                loading="lazy"
                className="absolute top-0 left-0 border-0 pointer-events-none"
                style={{
                  width: "1280px",
                  height: "800px",
                  transform: "scale(0.1406)",
                  transformOrigin: "top left",
                }}
                sandbox="allow-scripts allow-same-origin"
              />
            ) : (
              <div className="flex flex-col items-center justify-center gap-1 h-full">
                <div className="w-16 h-1.5 rounded-full bg-white/10" />
                <div className="w-12 h-1 rounded-full bg-white/[0.06]" />
                <div className="w-20 h-1 rounded-full bg-white/[0.06]" />
              </div>
            )}
          </div>
        </div>

        {/* Stats footer */}
        <div className="px-3 py-2 border-t border-white/[0.06] flex items-center justify-between">
          <div className="flex items-center gap-1">
            <ArrowDown className="w-3 h-3 text-emerald-400/70" />
            <span className="text-[11px] font-semibold text-emerald-400">
              {node.conversionRate}%
            </span>
          </div>
          {node.revenue !== undefined && node.revenue > 0 && (
            <span className="text-[10px] font-medium text-amber-400/80">
              {formatRevenue(node.revenue)}
            </span>
          )}
          <div className="flex items-center gap-1">
            <Users className="w-2.5 h-2.5 text-white/30" />
            <span className="text-[10px] text-white/50">{node.trafficOut.toLocaleString()}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
